/**
 * The PDF's small breakdown pies:
 *  - התפלגות לפי בסיסי הצמדה (Israeli bonds: CPI-linked vs shekel)
 *  - התפלגות חשיפה גיאוגרפית (equities: Israel vs abroad)
 *  - התפלגות אפיקים (whole portfolio: stocks / corp bonds / gov bonds / liquidity / foreign bonds / commodities)
 */
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Card, EmptyState } from './Card.js';
import { fmtPct } from '../utils.js';
import { useMoney } from '../money.js';
import { useChartAnimation } from '../printScope.js';
import { SERIES, PIE_COLORS, TOOLTIP_PROPS } from '../chartTheme.js';
import type { RikuzLineRow } from '@agio/core';

const TWO_SLICE_COLORS = [SERIES.portfolio, SERIES.positive];

function sliceHasData(slices: { value: number }[]) {
  return slices.some((s) => s.value > 0);
}

function SlicePie({
  title,
  slices,
  fundIds,
  colors = PIE_COLORS,
}: {
  title: string;
  slices: { name: string; value: number }[];
  /** The funds the slices were summed over — decides the currency shown. */
  fundIds: string[];
  colors?: readonly string[];
}) {
  const animate = useChartAnimation();
  const money = useMoney();
  const data = slices.filter((s) => s.value > 0);
  const total = data.reduce((a, s) => a + s.value, 0);
  if (total === 0) return <Card title={title}><EmptyState label="אין נתונים" /></Card>;

  return (
    <Card title={title}>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2} stroke="none" isAnimationActive={animate}>
            {data.map((d, i) => (
              <Cell key={d.name} fill={colors[i % colors.length]} />
            ))}
          </Pie>
          <Tooltip {...TOOLTIP_PROPS} formatter={(v: number, name: string) => [money(v, fundIds), name]} />
        </PieChart>
      </ResponsiveContainer>
      <ul className="mt-1 space-y-1 text-sm">
        {data.map((d, i) => (
          <li key={d.name} className="flex items-center gap-2">
            <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: colors[i % colors.length] }} />
            <span className="text-muted-foreground">{d.name}</span>
            <span className="ms-auto tabular-nums">{fmtPct(d.value / total, 1)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

const sum = (lines: RikuzLineRow[], sleeves: string[]) =>
  lines.filter((l) => sleeves.includes(l.sleeve)).reduce((a, l) => a + (l.value ?? 0), 0);

const linkageSlices = (lines: RikuzLineRow[]) => [
  { name: 'צמוד מדד', value: sum(lines, ['bonds_cpi_corp', 'bonds_cpi_gov']) },
  { name: 'שקלי', value: sum(lines, ['bonds_nis_corp', 'bonds_nis_gov', 'money_market']) },
];

export function LinkagePie({ lines }: { lines: RikuzLineRow[] }) {
  return <SlicePie title='התפלגות לפי בסיסי הצמדה — אג"ח ישראל' slices={linkageSlices(lines)} fundIds={lines.map((l) => l.fund_id)} colors={TWO_SLICE_COLORS} />;
}
export const linkageHasData = (lines: RikuzLineRow[]) => sliceHasData(linkageSlices(lines));

const geoSlices = (lines: RikuzLineRow[]) => [
  { name: 'מניות ישראל', value: sum(lines, ['stocks_il']) },
  { name: 'מניות חו"ל', value: sum(lines, ['stocks_foreign']) },
];

export function GeoPie({ lines }: { lines: RikuzLineRow[] }) {
  return <SlicePie title="התפלגות חשיפה גיאוגרפית — מניות" slices={geoSlices(lines)} fundIds={lines.map((l) => l.fund_id)} colors={TWO_SLICE_COLORS} />;
}
export const geoHasData = (lines: RikuzLineRow[]) => sliceHasData(geoSlices(lines));

/** Whole-portfolio asset-class split (the PDF's "התפלגות אפיקים" pie). */
const assetClassSlices = (lines: RikuzLineRow[]) => [
  { name: 'מניות', value: sum(lines, ['stocks_il', 'stocks_foreign']) },
  { name: 'אג"ח קונצרני', value: sum(lines, ['bonds_cpi_corp', 'bonds_nis_corp']) },
  { name: 'אג"ח ממשלתי', value: sum(lines, ['bonds_cpi_gov', 'bonds_nis_gov']) },
  { name: 'נזילות', value: sum(lines, ['money_market', 'cash_ils', 'cash_fx']) },
  { name: 'אג"ח חו"ל', value: sum(lines, ['bonds_foreign']) },
  { name: 'סחורות', value: sum(lines, ['commodities']) },
  { name: 'אלטרנטיבי', value: sum(lines, ['alternatives']) },
];

export function AssetClassPie({ lines }: { lines: RikuzLineRow[] }) {
  return <SlicePie title="התפלגות אפיקים" slices={assetClassSlices(lines)} fundIds={lines.map((l) => l.fund_id)} />;
}
export const assetClassHasData = (lines: RikuzLineRow[]) => sliceHasData(assetClassSlices(lines));
